import type { Aspect, Background, Design, Widget, WidgetStyle, WidgetType } from './types';
import { ASPECTS } from './types';
import { TEMPLATES, templateWidgets } from './templates';
import { GAPS } from './grid';
import type { BackgroundPattern } from './patterns';

/**
 * Style presets: a complete look (background and pattern, widget style and colours, fonts, and the design of each
 * widget, e.g. analog clocks). A style can restyle the current layout, or bring its own layout with it.
 */

export interface StylePreset {
  id: string;
  name: string;
  description: string;
  /** Layout used when the style is applied with its layout. */
  template: string;
  background: Background;
  widget: { style: WidgetStyle; fill?: string | null; opacity?: number; border?: { width: number; color: string } | null; textColor?: string | null };
  accent: string;
  font: string;
  /** Fonts for particular widgets (e.g. a digital clock face). */
  fonts?: Partial<Record<WidgetType, string>>;
  weight?: 400 | 500 | 600 | 700 | 800;
  /** Design per widget type (see WIDGET_DESIGNS). */
  designs?: Partial<Record<WidgetType, string>>;
  /** Prayer time tiles. */
  tile?: string | null;
  gap?: number;
  /** A different widget style for particular widgets (e.g. a clock with no panel), so a style can mix looks. */
  styles?: Partial<Record<WidgetType, WidgetStyle>>;
  /** Corner radius for every widget (px at the native size). */
  radius?: number;
  /** Where it's inspired by, shown with the description. */
  place?: string;
  /** Text colour of particular widgets (e.g. the clock's digits in the style's colour). */
  inks?: Partial<Record<WidgetType, string>>;
  /** The font and colour of the numbers in the clock, countdowns and prayer times (dates and labels keep the style's font). */
  digits?: { font?: string; color?: string };
  /** Shown first for portrait screens (made for them). */
  portrait?: boolean;
}

const p = (x: BackgroundPattern): BackgroundPattern => x;

/** The classic black digital board, in the colours masjids choose. */
const board = (id: string, name: string, colour: string, description: string): StylePreset => ({
  id, name, description, template: 'board', portrait: true,
  background: { kind: 'solid', color: '#000000' },
  widget: { style: 'none' }, accent: colour, font: 'Inter', weight: 600,
  digits: { font: 'Seven Segment', color: colour },
  designs: { clock: 'digital', prayerTimes: 'board', countdown: 'digital', ticker: 'plain' }, gap: GAPS.small,
});

export const STYLE_PRESETS: StylePreset[] = [
  board('board-green', 'Digital Board', '#39FF6A', 'The classic masjid board: green digital numbers on black, every salah’s start and jama‘ah.'),
  board('board-red', 'Digital Board Red', '#FF453A', 'The digital board with red numbers, bright and easy to read from the back.'),
  board('board-white', 'Digital Board White', '#FFFFFF', 'The digital board in plain white on black.'),
  board('board-amber', 'Digital Board Amber', '#FFB340', 'The digital board in warm amber, softer on the eyes at night.'),
  {
    id: 'board-glass', name: 'Glass Board', description: 'The prayer board in NoorView’s frosted glass, with large, light numbers.', template: 'board', portrait: true,
    background: { kind: 'gradient', color: '#0A84FF', color2: '#020A1C' },
    widget: { style: 'glass' }, accent: '#0A84FF', font: 'SF Pro Display', radius: 28,
    digits: { font: 'SF Pro Display' },
    designs: { clock: 'digital', prayerTimes: 'board', countdown: 'ring', ticker: 'tag' }, gap: GAPS.medium,
  },
  {
    id: 'board-paper', name: 'Paper Board', description: 'Clean black on white, like a printed timetable: calm in a bright room.', template: 'board', portrait: true,
    background: { kind: 'solid', color: '#F5F5F7' },
    widget: { style: 'flat', fill: '#FFFFFF' }, accent: '#0071E3', font: 'Inter', radius: 22,
    digits: { font: 'Space Grotesk', color: '#1D1D1F' },
    designs: { clock: 'digital', prayerTimes: 'board', countdown: 'bar', ticker: 'plain' }, gap: GAPS.medium,
  },
  {
    id: 'board-walnut', name: 'Walnut & Gold', description: 'Dark walnut and gold with a khatam border: the traditional wooden board, modernised.', template: 'board', portrait: true,
    background: { kind: 'gradient', color: '#3A2412', color2: '#0E0804', pattern: p({ id: 'khatam', color: '#E0B354', opacity: 14, coverage: 'border', size: 'small' }) },
    widget: { style: 'flat', fill: '#1E130A', border: { width: 2, color: '#6B4A1E' } }, accent: '#E0B354', font: 'Playfair Display', radius: 14,
    digits: { font: 'Oswald', color: '#F3D58A' },
    designs: { clock: 'digital', prayerTimes: 'board', countdown: 'digital', ticker: 'plain' }, gap: GAPS.small,
  },
  {
    id: 'board-dials', name: 'Dial Board', description: 'A dial for every salah down the screen, in cream and deep blue.', template: 'board', portrait: true,
    background: { kind: 'solid', color: '#F2EBDD' },
    widget: { style: 'flat', fill: '#FFFDF8' }, accent: '#1E3A8A', font: 'DM Sans', radius: 18,
    designs: { clock: 'analog-minimal', prayerTimes: 'clocks', countdown: 'ring', ticker: 'plain' }, gap: GAPS.small,
  },
  {
    id: 'big-clock', name: 'Big Clock', description: 'Just the time and both dates in big green digits: for a hall or a corridor.', template: 'clock-only', portrait: true,
    background: { kind: 'solid', color: '#000000' },
    widget: { style: 'none' }, accent: '#39FF6A', font: 'Inter', weight: 600,
    digits: { font: 'Seven Segment', color: '#39FF6A' },
    designs: { clock: 'digital' }, gap: GAPS.small,
  },
  {
    id: 'night-sky', name: 'Night Sky', description: 'Black and orange only. Modern, sleek and calm after dark.', template: 'classic',
    background: { kind: 'gradient', color: '#1A0E02', color2: '#000000' },
    widget: { style: 'flat', fill: '#0D0D0D', border: { width: 2, color: '#2A1A08' } }, accent: '#FF9F0A', font: 'Sora',
    designs: { clock: 'stacked', prayerTimes: 'table', countdown: 'ring', donations: 'ring', events: 'timeline', ticker: 'plain', ayah: 'quote' }, gap: GAPS.medium,
  },
  {
    id: 'signature', name: 'Signature', description: 'Analog clocks and faces throughout, in navy and gold.', template: 'corridor',
    background: { kind: 'gradient', color: '#13284A', color2: '#050B16', pattern: p({ id: 'khatam', color: '#C9A227', opacity: 10, coverage: 'border', size: 'medium' }) },
    widget: { style: 'glass' }, accent: '#C9A227', font: 'Playfair Display',
    designs: { clock: 'analog', prayerTimes: 'clocks', countdown: 'ring', donations: 'ring', jumuah: 'focus', ayah: 'quote', hadith: 'quote', events: 'cards', ticker: 'pills' }, gap: GAPS.medium,
  },
  {
    id: 'noorview', name: 'NoorView Blue', description: 'NoorView’s own frosted glass on deep blue.', template: 'classic',
    background: { kind: 'gradient', color: '#0A84FF', color2: '#020A1C' },
    widget: { style: 'glass' }, accent: '#0A84FF', font: 'SF Pro Display', designs: { clock: 'digital', prayerTimes: 'tiles' }, gap: GAPS.medium,
  },
  {
    id: 'emerald', name: 'Emerald', description: 'Deep green and gold with zellige in the corners.', template: 'knowledge',
    background: { kind: 'gradient', color: '#0E5E4E', color2: '#02130F', pattern: p({ id: 'zellige', color: '#E7C35A', opacity: 18, coverage: 'corners', size: 'medium' }) },
    widget: { style: '3d', fill: '#0B3D32' }, accent: '#E7C35A', font: 'Manrope',
    designs: { clock: 'analog-arabic', prayerTimes: 'icons', countdown: 'digital', ayah: 'centered', hadith: 'centered' }, gap: GAPS.medium,
  },
  {
    id: 'marble', name: 'Marble', description: 'Light, bright and elegant, with bronze highlights.', template: 'split',
    background: { kind: 'solid', color: '#F4F1EA', pattern: p({ id: 'arches', color: '#B08D57', opacity: 8, coverage: 'full', size: 'large' }) },
    widget: { style: '3d', fill: '#FFFFFF' }, accent: '#B08D57', font: 'Lexend',
    designs: { clock: 'analog-minimal', prayerTimes: 'tiles', announcements: 'list' }, gap: GAPS.medium,
  },
  {
    id: 'ramadan', name: 'Ramadan Nights', description: 'Purple and gold, Iftar and Sehri up front and stars all round.', template: 'ramadan',
    background: { kind: 'gradient', color: '#2A0F4C', color2: '#0B0418', pattern: p({ id: 'hexstar', color: '#E7C35A', opacity: 9, coverage: 'full', size: 'medium' }) },
    widget: { style: 'glass' }, accent: '#E7C35A', font: 'Outfit',
    designs: { clock: 'digital', prayerTimes: 'icons', countdown: 'hourglass', ticker: 'pills', iftar: 'countdown' }, gap: GAPS.medium,
  },
  {
    id: 'desert', name: 'Desert Sand', description: 'Warm sand and terracotta, with a Moroccan border.', template: 'community',
    background: { kind: 'solid', color: '#E9DCC3', pattern: p({ id: 'moroccan', color: '#9A3412', opacity: 14, coverage: 'border', size: 'small' }) },
    widget: { style: 'flat', fill: '#FFF8EC' }, accent: '#C2410C', font: 'Outfit',
    designs: { clock: 'digital', prayerTimes: 'tiles', events: 'cards', announcements: 'list' }, gap: GAPS.medium,
  },
  {
    id: 'midnight', name: 'Midnight Glass', description: 'Black with a cool blue glow and crisp glass panels.', template: 'prayer-focus',
    background: { kind: 'gradient', color: '#0A2A4A', color2: '#000000' },
    widget: { style: 'glass', opacity: 85 }, accent: '#64D2FF', font: 'Space Grotesk',
    designs: { clock: 'digital', prayerTimes: 'focus', countdown: 'ring' }, gap: GAPS.large,
  },
  {
    id: 'mono', name: 'Monochrome', description: 'Pure black and white. Bold condensed type, nothing else.', template: 'minimal',
    background: { kind: 'solid', color: '#000000' },
    widget: { style: 'none' }, accent: '#FFFFFF', font: 'Oswald', weight: 600,
    designs: { clock: 'digital', prayerTimes: 'table', countdown: 'digital' }, gap: GAPS.small,
  },
  {
    id: 'digital', name: 'Digital', description: 'A green seven-segment clock and techy type, like a station board.', template: 'corridor',
    background: { kind: 'solid', color: '#050806' },
    widget: { style: 'flat', fill: '#0B120D', border: { width: 2, color: '#173A22' } }, accent: '#30D158', font: 'Chakra Petch',
    fonts: { clock: 'Seven Segment', countdown: 'Seven Segment' },
    designs: { clock: 'digital', prayerTimes: 'table', countdown: 'digital', jumuah: 'focus', events: 'list', ticker: 'tag' }, gap: GAPS.small,
  },
  {
    id: 'ocean', name: 'Ocean', description: 'Teal and soft white, rounded type and friendly icons.', template: 'classic',
    background: { kind: 'gradient', color: '#0E4D64', color2: '#021A24', pattern: p({ id: 'circles', color: '#64D2FF', opacity: 7, coverage: 'full', size: 'large' }) },
    widget: { style: 'glass' }, accent: '#64D2FF', font: 'Nunito',
    designs: { clock: 'analog-minimal', prayerTimes: 'icons', donations: 'segments' }, gap: GAPS.medium,
  },
  {
    id: 'kufic-gold', name: 'Kufic Gold', description: 'Black and gold with a Kufic border, for special occasions.', template: 'jumuah',
    background: { kind: 'solid', color: '#0A0805', pattern: p({ id: 'kufic', color: '#D4AF37', opacity: 22, coverage: 'border', size: 'small' }) },
    widget: { style: 'legacy', fill: '#15110A', border: { width: 2, color: '#3B2F12' } }, accent: '#D4AF37', font: 'Reem Kufi',
    designs: { clock: 'analog-arabic', prayerTimes: 'clocks', jumuah: 'focus', countdown: 'flip' }, gap: GAPS.medium,
  },
  {
    id: 'makkah', name: 'Makkah', place: 'Masjid al-Haram', description: 'The Kiswah’s black and gold, with a khatam border and a clock that floats free.', template: 'hero-clock',
    background: { kind: 'gradient', color: '#1C160B', color2: '#000000', pattern: p({ id: 'khatam', color: '#D4AF37', opacity: 16, coverage: 'border', size: 'small' }) },
    widget: { style: 'flat', fill: '#0E0C08', border: { width: 2, color: '#5C4A1C' } }, accent: '#D4AF37', font: 'Playfair Display', fonts: { ayah: 'Amiri', hadith: 'Amiri' },
    styles: { clock: 'none', countdown: 'none' }, radius: 10,
    designs: { clock: 'analog-arabic', prayerTimes: 'table', countdown: 'digital', ayah: 'centered', hadith: 'quote', ticker: 'plain' }, gap: GAPS.medium,
  },
  {
    id: 'madinah', name: 'Madinah', place: 'Masjid an-Nabawi', description: 'The Green Dome’s green with soft white and a rosette in each corner.', template: 'classic',
    background: { kind: 'gradient', color: '#1E7B4F', color2: '#03160D', pattern: p({ id: 'rosette', color: '#F1F5EE', opacity: 12, coverage: 'corners', size: 'large' }) },
    widget: { style: '3d', fill: '#0C3B26' }, accent: '#F3E3B5', font: 'Manrope', radius: 22,
    designs: { clock: 'analog-minimal', prayerTimes: 'icons', countdown: 'ring', jumuah: 'focus', ayah: 'centered' }, gap: GAPS.medium,
  },
  {
    id: 'aqsa', name: 'Al-Aqsa', place: 'Jerusalem', description: 'The Dome of the Rock’s blue tilework crowned in gold.', template: 'twin',
    background: { kind: 'gradient', color: '#1747A6', color2: '#040F2A', pattern: p({ id: 'hexstar', color: '#E8B54A', opacity: 10, coverage: 'full', size: 'medium' }) },
    widget: { style: 'glass' }, accent: '#E8B54A', font: 'Lexend', radius: 28,
    designs: { clock: 'analog', prayerTimes: 'tiles', countdown: 'ring', donations: 'ring' }, gap: GAPS.medium,
  },
  {
    id: 'alhambra', name: 'Alhambra', place: 'Granada', description: 'Warm plaster and cedar, outlined panels and a carved lattice.', template: 'split',
    background: { kind: 'solid', color: '#F1E4CE', pattern: p({ id: 'lattice', color: '#8C3B1E', opacity: 9, coverage: 'full', size: 'medium' }) },
    widget: { style: 'none', border: { width: 3, color: '#B4825A' } }, accent: '#8C3B1E', font: 'Playfair Display', radius: 6,
    designs: { clock: 'analog-arabic', prayerTimes: 'table', announcements: 'list', ticker: 'plain' }, gap: GAPS.medium,
  },
  {
    id: 'blue-mosque', name: 'Blue Mosque', place: 'Istanbul', description: 'Iznik blues and turquoise, with a dial for every salah.', template: 'corridor',
    background: { kind: 'gradient', color: '#1E4E8C', color2: '#061428', pattern: p({ id: 'rosette', color: '#7DD3FC', opacity: 8, coverage: 'full', size: 'medium' }) },
    widget: { style: '3d', fill: '#0F2D57' }, accent: '#4FD1C5', font: 'DM Sans', radius: 18,
    designs: { clock: 'analog', prayerTimes: 'clocks', countdown: 'hourglass', jumuah: 'focus', events: 'timeline' }, gap: GAPS.medium,
  },
  {
    id: 'isfahan', name: 'Isfahan', place: 'Shah Mosque', description: 'Persian turquoise and saffron with a Kufic-style face.', template: 'prayer-focus',
    background: { kind: 'gradient', color: '#0E7490', color2: '#03222B', pattern: p({ id: 'khatam', color: '#FACC15', opacity: 12, coverage: 'border', size: 'medium' }) },
    widget: { style: 'glass', opacity: 90 }, accent: '#FACC15', font: 'Reem Kufi', radius: 24,
    designs: { clock: 'stacked', prayerTimes: 'icons', countdown: 'flip' }, gap: GAPS.medium,
  },
  {
    id: 'cordoba', name: 'Córdoba', place: 'The Mezquita', description: 'Red and cream arches, flat panels and classic serif type.', template: 'posters',
    background: { kind: 'gradient', color: '#3A0F0B', color2: '#0D0303', pattern: p({ id: 'arches', color: '#F5E6D3', opacity: 9, coverage: 'full', size: 'large' }) },
    widget: { style: 'flat', fill: '#1E0A07' }, accent: '#E0533D', font: 'Playfair Display', radius: 4,
    designs: { clock: 'analog-minimal', prayerTimes: 'table', ticker: 'pills' }, gap: GAPS.small,
  },
  {
    id: 'zayed', name: 'Sheikh Zayed', place: 'Abu Dhabi', description: 'White marble and gold, bright and calm, with raised panels.', template: 'knowledge',
    background: { kind: 'solid', color: '#F7F6F2', pattern: p({ id: 'rosette', color: '#B8860B', opacity: 12, coverage: 'corners', size: 'large' }) },
    widget: { style: '3d', fill: '#FFFFFF' }, accent: '#B8860B', font: 'Lexend', radius: 26,
    designs: { clock: 'analog-minimal', prayerTimes: 'icons', ayah: 'centered', hadith: 'centered' }, gap: GAPS.large,
  },
  {
    id: 'badshahi', name: 'Badshahi', place: 'Lahore', description: 'Red sandstone and white marble inlay, with a jaali border.', template: 'jumuah',
    background: { kind: 'gradient', color: '#7A2E1F', color2: '#240A05', pattern: p({ id: 'lattice', color: '#F8E7C9', opacity: 14, coverage: 'border', size: 'small' }) },
    widget: { style: 'flat', fill: '#3B140C', border: { width: 2, color: '#6E3324' } }, accent: '#F8E7C9', font: 'Montserrat', radius: 12,
    designs: { clock: 'digital', prayerTimes: 'tiles', jumuah: 'times', countdown: 'digital' }, gap: GAPS.medium,
  },
  {
    id: 'fez', name: 'Fez', place: 'Al-Qarawiyyin', description: 'Green tiles and cedar gold, no panels, just the pattern.', template: 'minimal',
    background: { kind: 'gradient', color: '#166534', color2: '#03200E', pattern: p({ id: 'moroccan', color: '#FDE68A', opacity: 10, coverage: 'full', size: 'medium' }) },
    widget: { style: 'none' }, accent: '#FDE68A', font: 'Outfit',
    designs: { clock: 'digital', prayerTimes: 'icons', countdown: 'digital' }, gap: GAPS.medium,
  },
  {
    id: 'damascus', name: 'Damascus', place: 'The Umayyad Mosque', description: 'Gold mosaic on deep green, glass panels and Naskh scripture.', template: 'notice-board',
    background: { kind: 'gradient', color: '#0F4D3B', color2: '#021510', pattern: p({ id: 'zellige', color: '#F0C75E', opacity: 14, coverage: 'corners', size: 'medium' }) },
    widget: { style: 'glass', border: { width: 2, color: '#6B5A24' } }, accent: '#F0C75E', font: 'DM Sans', fonts: { ayah: 'Amiri', hadith: 'Amiri' }, radius: 20,
    designs: { clock: 'analog-arabic', prayerTimes: 'table', announcements: 'slides', events: 'cards', hadith: 'quote' }, gap: GAPS.medium,
  },
  {
    id: 'casablanca', name: 'Casablanca', place: 'Hassan II Mosque', description: 'Atlantic blue and cedar, with a Kufic border and big digits.', template: 'twin',
    background: { kind: 'gradient', color: '#0C4A6E', color2: '#031926', pattern: p({ id: 'kufic', color: '#7DD3FC', opacity: 14, coverage: 'border', size: 'small' }) },
    widget: { style: '3d', fill: '#0A3350' }, accent: '#38BDF8', font: 'Urbanist', radius: 16,
    styles: { clock: 'none' },
    designs: { clock: 'flip', countdown: 'flip', prayerTimes: 'tiles' }, gap: GAPS.medium,
  },
  {
    id: 'muscat', name: 'Muscat', place: 'Sultan Qaboos Grand Mosque', description: 'Sandstone cream with deep blue, crisp and easy to read.', template: 'sidebar',
    background: { kind: 'solid', color: '#EFE7DA', pattern: p({ id: 'khatam', color: '#1E3A8A', opacity: 7, coverage: 'full', size: 'medium' }) },
    widget: { style: 'flat', fill: '#FFFFFF' }, accent: '#1E3A8A', font: 'Plus Jakarta Sans', radius: 14,
    designs: { clock: 'digital', prayerTimes: 'table', announcements: 'list', jumuah: 'focus' }, gap: GAPS.medium,
  },
];

/** Gives every widget the style's look, keeping each widget's own content and position. */
export function applyStyle(design: Design, s: StylePreset): Design {
  const widgets: Widget[] = design.widgets.map((w) => {
    const plain = w.type === 'logo' || w.type === 'posters';
    const props: Record<string, unknown> = { ...w.props };
    const dz = s.designs?.[w.type];
    if (dz) props.design = dz;
    if (w.type === 'prayerTimes') { props.tileColor = s.tile ?? null; props.tileText = null; props.prayerColors = {}; }
    if (w.type === 'prayerTimes' || w.type === 'clock' || w.type === 'countdown') { props.digitFont = s.digits?.font ?? null; props.digitColor = s.digits?.color ?? null; }
    const next: Widget = {
      ...w,
      style: plain && w.type === 'logo' ? w.style : s.styles?.[w.type] ?? s.widget.style,
      accent: s.accent,
      opacity: w.type === 'logo' ? w.opacity : s.widget.opacity ?? 100,
      text: { ...w.text, font: s.fonts?.[w.type] ?? s.font, ...(s.weight ? { weight: s.weight } : {}) },
      props,
    };
    const own = s.styles?.[w.type];
    if (s.radius != null) next.radius = s.radius; else delete next.radius;
    if (s.widget.fill && !plain && !own) next.fill = s.widget.fill; else delete next.fill;
    const ink = s.inks?.[w.type] ?? s.widget.textColor;
    if (ink) next.textColor = ink; else delete next.textColor;
    if (s.widget.border && !plain && own !== 'none') next.border = s.widget.border; else delete next.border;
    return next;
  });
  return { ...design, background: { ...s.background }, gap: s.gap ?? design.gap, widgets };
}

/** The style with its own layout, on the design's screen shape. */
/** The styles in the order to show them: on a portrait screen, the ones made for portrait come first. */
export function stylesFor(aspect: Aspect): StylePreset[] {
  const a = ASPECTS[aspect];
  if (!a || a.height <= a.width) return STYLE_PRESETS;
  return [...STYLE_PRESETS.filter((s) => s.portrait), ...STYLE_PRESETS.filter((s) => !s.portrait)];
}

export function applyStyleWithLayout(design: Design, s: StylePreset): Design {
  const t = TEMPLATES.find((x) => x.id === s.template) ?? TEMPLATES[0]!;
  return applyStyle({ ...design, widgets: templateWidgets(t, design) }, s);
}
